/**
 * 列表页视图设置共享状态：筛选字段 + 表格列的可见性与顺序。
 *
 * SearchBar 与 DataTable 通过同一 `settingsKey` 获得同一份响应式状态。
 * 持久化按组写入独立 localStorage 键（cms:searchbar:<key> / cms:datatable:<key>）。
 * user-view-settings-persistence 升级：模块级注入 viewSettingsApi（get/save）后，
 * 初始化异步读服务端（命中即用，localStorage 仅作离线/降级缓存），
 * 保存写本地 + 异步同步服务端；无注入 / 无会话 / 服务端失败时回退纯 localStorage。
 */
import { reactive } from 'vue'

export interface SettingsGroup {
  order: string[]
  hidden: string[]
  hydrated: boolean
  /** 用户经设置弹层调整的列样式覆盖（列模型声明的默认值之上叠加）。 */
  styles?: Record<string, ColumnStyleOverride>
  /** 服务端数据是否已应用（仅应用一次，避免覆盖用户会话内编辑）。 */
  serverHydrated?: boolean
}

export interface ColumnStyleOverride {
  width?: number
  bold?: boolean
  color?: string
}

export interface ViewSettingsState {
  field: SettingsGroup
  column: SettingsGroup
}

/** 服务端视图设置读写契约（由宿主应用注入，@cms/ui 不依赖业务 API）。 */
export interface ViewSettingsApi {
  get(settingsKey: string): Promise<ViewSettingsServerRecord | null>
  save(input: ViewSettingsSavePayload): Promise<unknown>
}

export interface ViewSettingsServerRecord {
  settingsKey: string
  fieldJson: string | null
  columnJson: string | null
}

export interface ViewSettingsSavePayload {
  settingsKey: string
  fieldJson?: string | null
  columnJson?: string | null
}

const registry = new Map<string, ViewSettingsState>()
const serverData = new Map<string, ViewSettingsServerRecord>()
const serverFetching = new Map<string, Promise<void>>()
const serverPayloadCache = new Map<string, { fieldJson?: string; columnJson?: string }>()

let viewSettingsApi: ViewSettingsApi | null = null

export function configureViewSettingsApi(api: ViewSettingsApi | null): void {
  viewSettingsApi = api
  if (!api) {
    serverData.clear()
    serverFetching.clear()
    serverPayloadCache.clear()
  }
}

export function getViewSettings(settingsKey: string): ViewSettingsState {
  let state = registry.get(settingsKey)
  if (!state) {
    state = reactive({
      field: { order: [], hidden: [], hydrated: false },
      column: { order: [], hidden: [], hydrated: false },
    })
    registry.set(settingsKey, state)
  }
  return state
}

function settingsKeyFromStorageKey(storageKey: string): string {
  return storageKey.replace(/^cms:(searchbar|datatable):/, '')
}

function isFieldGroup(storageKey: string): boolean {
  return storageKey.startsWith('cms:searchbar:')
}

function ensureServerHydration(settingsKey: string): void {
  if (!viewSettingsApi || serverFetching.has(settingsKey)) return
  const promise = viewSettingsApi
    .get(settingsKey)
    .then((record) => {
      if (record) {
        serverData.set(settingsKey, record)
        serverPayloadCache.set(settingsKey, {
          fieldJson: record.fieldJson ?? undefined,
          columnJson: record.columnJson ?? undefined,
        })
        applyServerDataToStates(settingsKey)
      }
    })
    .catch(() => {})
  serverFetching.set(settingsKey, promise)
}

function applyServerDataToStates(settingsKey: string): void {
  const state = registry.get(settingsKey)
  if (!state) return
  applyServerGroup(state.field, 'field', settingsKey)
  applyServerGroup(state.column, 'column', settingsKey)
}

function applyServerGroup(group: SettingsGroup, kind: 'field' | 'column', settingsKey: string): void {
  if (group.serverHydrated) return
  const record = serverData.get(settingsKey)
  if (!record) return
  const present = new Set(group.order)
  if (present.size === 0) return
  const json = kind === 'field' ? record.fieldJson : record.columnJson
  group.serverHydrated = true
  if (!json) return
  try {
    const data = JSON.parse(json) as { order?: string[]; hidden?: string[]; styles?: Record<string, ColumnStyleOverride> }
    if (Array.isArray(data.order)) {
      const serverOrder = data.order.filter((k) => present.has(k))
      group.order = [...serverOrder, ...group.order.filter((k) => !serverOrder.includes(k))]
    }
    if (Array.isArray(data.hidden)) {
      group.hidden = data.hidden.filter((k) => present.has(k))
    }
    if (data.styles) {
      group.styles = group.styles ?? {}
      for (const [key, value] of Object.entries(data.styles)) {
        if (present.has(key)) group.styles[key] = value
      }
    }
  } catch {
    /* 忽略损坏的服务端数据 */
  }
}

/**
 * 将一组源的当前键合并进组的共享顺序（保留已存在键的顺序），剔除已不存在的隐藏项。
 * 首次调用（组尚未 hydrated 且提供 storageKey）时从存储恢复该组的 order/hidden；
 * 同时触发（并应用）该 settingsKey 的服务端设置（服务端优先，本地仅作降级缓存）。
 */
export function syncSettingsGroup(
  group: SettingsGroup,
  sourceKeys: string[],
  storageKey: string | null,
): void {
  const existing = new Set(group.order)
  group.order = [...group.order.filter((k) => sourceKeys.includes(k)), ...sourceKeys.filter((k) => !existing.has(k))]
  const present = new Set(sourceKeys)
  group.hidden = group.hidden.filter((k) => present.has(k))
  if (group.styles) {
    for (const key of Object.keys(group.styles)) {
      if (!present.has(key)) delete group.styles[key]
    }
  }

  if (!group.hydrated) {
    group.hydrated = true
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          const data = JSON.parse(saved) as { order?: string[]; hidden?: string[]; styles?: Record<string, ColumnStyleOverride> }
          if (Array.isArray(data.order)) group.order = data.order.filter((k) => present.has(k))
          if (Array.isArray(data.hidden)) group.hidden = data.hidden.filter((k) => present.has(k))
          if (data.styles) {
            group.styles = group.styles ?? {}
            for (const [key, value] of Object.entries(data.styles)) {
              if (present.has(key)) group.styles[key] = value
            }
          }
        }
      } catch {
        /* 忽略损坏的持久化数据 */
      }
    }
  }

  if (storageKey) {
    const settingsKey = settingsKeyFromStorageKey(storageKey)
    ensureServerHydration(settingsKey)
    applyServerGroup(group, isFieldGroup(storageKey) ? 'field' : 'column', settingsKey)
  }
}

export function persistSettingsGroup(group: SettingsGroup, storageKey: string): void {
  if (!group.hydrated) return
  if (!group.order.length && !group.hidden.length && !group.styles) return
  const payload: { order: string[]; hidden: string[]; styles?: Record<string, ColumnStyleOverride> } = {
    order: group.order,
    hidden: group.hidden,
  }
  if (group.styles && Object.keys(group.styles).length) payload.styles = group.styles
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(payload))
  } catch {
    // 无存储能力（SSR、隐私模式或单测环境）时仍允许服务端同步，不阻塞界面更新。
  }
  pushToServer(storageKey, payload)
}

function pushToServer(
  storageKey: string,
  payload: { order: string[]; hidden: string[]; styles?: Record<string, ColumnStyleOverride> },
): void {
  if (!viewSettingsApi) return
  const settingsKey = settingsKeyFromStorageKey(storageKey)
  const kind = isFieldGroup(storageKey) ? 'field' : 'column'
  const pending = serverFetching.get(settingsKey)
  const run = (): void => {
    const cached = serverPayloadCache.get(settingsKey) ?? {}
    const next = kind === 'field'
      ? { ...cached, fieldJson: JSON.stringify(payload) }
      : { ...cached, columnJson: JSON.stringify(payload) }
    serverPayloadCache.set(settingsKey, next)
    void viewSettingsApi!.save({ settingsKey, fieldJson: next.fieldJson, columnJson: next.columnJson }).catch(() => {})
  }
  if (pending) {
    void pending.then(run)
  } else {
    run()
  }
}

export function resetSettingsGroup(group: SettingsGroup, sourceKeys: string[]): void {
  group.order = [...sourceKeys]
  group.hidden = []
  group.styles = {}
}

/** 仅测试用：清空跨组件共享的 registry 与服务端状态，避免用例间状态串扰。 */
export function clearViewSettingsRegistry(): void {
  registry.clear()
  serverData.clear()
  serverFetching.clear()
  serverPayloadCache.clear()
  viewSettingsApi = null
}
